import { useEffect, useState } from 'react';
import SEO, { breadcrumbSchema, contactPageSchema } from '@/components/SEO';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Phone, Mail, MapPin, Send, Clock, Check, Music, Award, MessageCircle } from 'lucide-react';
import { buildWhatsAppUrl } from '@/components/WhatsAppLink';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import eventAward2023 from '@/assets/event-award-2023.png';
import eventAward2024 from '@/assets/event-award-2024.png';
import eventAward2025 from '@/assets/event-award-2025.png';
import eventAward2026 from '@/assets/event-award-2026.png';

const inputClass =
  'w-full px-4 py-3 rounded-lg border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/40 transition placeholder:text-muted-foreground/50';

const labelClass = 'block text-xs font-heading font-medium text-foreground mb-1.5';

const Contact = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sending, setSending] = useState(false);

  const [form, setForm] = useState({
    lastName: '',
    firstName: '',
    company: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    // Champs spécifiques artistes
    stageName: '',
    address: '',
    postalCode: '',
    city: '',
    website: '',
    socials: '',
    artisticDomain: '',
    mediaLink: '',
  });

  useEffect(() => {
    const subject = searchParams.get('subject');
    if (subject) setForm((f) => ({ ...f, subject }));
  }, [searchParams]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);

    const { data, error } = await supabase.functions.invoke('send-contact-email', {
      body: form,
    });

    if (error || data?.emailSent === false) {
      toast({
        title: 'Envoi impossible',
        description: 'Votre message n’a pas pu être envoyé. Merci de réessayer ou de nous contacter directement par email.',
        variant: 'destructive',
      });
      setSending(false);
      return;
    }

    toast({
      title: `Merci ${form.firstName} !`,
      description: 'Votre message a bien été envoyé. Nous reviendrons vers vous sous 48 à 72h.',
      action: (
        <div className="shrink-0 w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center">
          <Check size={18} className="text-primary" strokeWidth={3} />
        </div>
      ),
    });

    navigate('/confirmation', { state: { form } });
  };

  return (
    <>
      <SEO
        title="Contact — Cygnature Production | Devis gratuit événementiel"
        description="Contactez Cygnature Production pour votre événement. Devis gratuit sous 48 à 72h. Troyes, Grand Est et partout en France."
        path="/contact"
        keywords={['contact agence événementielle','devis mariage','demande de devis']}
        jsonLd={[
          breadcrumbSchema([{ name: 'Accueil', path: '/' }, { name: 'Contact', path: '/contact' }]),
          contactPageSchema(),
        ]}
      />
      {/* Hero */}
      <section className="px-6 lg:px-8 py-20 md:py-28 bg-petrol text-petrol-foreground">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-heading text-3xl md:text-5xl font-bold">
            Contactez-nous
          </h1>
          <p className="mt-5 text-petrol-foreground/60 max-w-2xl text-sm md:text-base leading-relaxed">
            Un événement en tête ? Parlons-en. Premier échange gratuit et sans engagement. Nous vous répondons sous 48 à 72h.
          </p>
        </div>
      </section>

      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-16">
          {/* Form */}
          <div className="lg:col-span-3">
            <h2 className="font-heading text-lg font-bold text-foreground mb-1">Formulaire de contact</h2>
            <p className="text-xs text-muted-foreground mb-8">Les champs marqués d'un * sont obligatoires</p>

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="lastName" className={labelClass}>Nom *</label>
                  <input type="text" id="lastName" name="lastName" value={form.lastName} onChange={handleChange} className={inputClass} placeholder="Dupont" required />
                </div>
                <div>
                  <label htmlFor="firstName" className={labelClass}>Prénom *</label>
                  <input type="text" id="firstName" name="firstName" value={form.firstName} onChange={handleChange} className={inputClass} placeholder="Jean" required />
                </div>
                {form.subject !== 'artiste' && (
                  <div>
                    <label htmlFor="company" className={labelClass}>Société / Occasion</label>
                    <input type="text" id="company" name="company" value={form.company} onChange={handleChange} className={inputClass} placeholder="Ex: Mariage, Gala, Entreprise..." />
                  </div>
                )}
                <div>
                  <label htmlFor="email" className={labelClass}>Email *</label>
                  <input type="email" id="email" name="email" value={form.email} onChange={handleChange} className={inputClass} placeholder="jean@exemple.fr" required />
                </div>
                <div>
                  <label htmlFor="phone" className={labelClass}>Téléphone{form.subject === 'artiste' ? ' *' : ''}</label>
                  <input type="tel" id="phone" name="phone" value={form.phone} onChange={handleChange} className={inputClass} placeholder="06 12 34 56 78" required={form.subject === 'artiste'} />
                </div>
                <div className={form.subject === 'artiste' ? 'md:col-span-2' : ''}>
                  <label htmlFor="subject" className={labelClass}>Type de prestation</label>
                   <select id="subject" name="subject" value={form.subject} onChange={handleChange} className={inputClass}>
                     <option value="">Sélectionner</option>
                     <option value="evenement">Événements publics & privés</option>
                     <option value="production">Booking artistes & spectacles</option>
                     <option value="mariage">Wedding Planner & Designer</option>
                     <option value="ceremonie">Officiant de cérémonies laïques</option>
                     <option value="enfants">Animations enfants & famille</option>
                     <option value="musique">Musique & ambiance</option>
                     <option value="lieux">Lieux & services associés</option>
                     <option value="animations">Animations spéciales & expériences</option>
                     <option value="a-la-location">À la location (catalogue)</option>
                     <option value="feu-de-jour">Feu de jour</option>
                     <option value="mascottes">Mascottes</option>
                     <option value="materiels">Matériels (son, lumière, mobilier)</option>
                     <option value="photobooth">Photobooth & borne à selfie</option>
                     <option value="artiste">Proposer mes services en tant qu'artiste</option>
                     <option value="autre">Autre</option>
                   </select>
                </div>
              </div>

              {form.subject === 'artiste' && (
                <div className="space-y-6 p-5 rounded-xl border border-primary/20 bg-primary/5">
                  <div className="flex items-center gap-2">
                    <Music size={16} className="text-primary" strokeWidth={1.5} />
                    <h3 className="font-heading font-semibold text-sm text-foreground">Votre profil artistique</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label htmlFor="stageName" className={labelClass}>Nom de scène</label>
                      <input type="text" id="stageName" name="stageName" value={form.stageName} onChange={handleChange} className={inputClass} placeholder="Votre nom d'artiste" />
                    </div>
                    <div className="md:col-span-2">
                      <label htmlFor="address" className={labelClass}>Adresse</label>
                      <input type="text" id="address" name="address" value={form.address} onChange={handleChange} className={inputClass} placeholder="Numéro et rue" />
                    </div>
                    <div>
                      <label htmlFor="postalCode" className={labelClass}>Code postal</label>
                      <input type="text" id="postalCode" name="postalCode" value={form.postalCode} onChange={handleChange} className={inputClass} placeholder="10000" />
                    </div>
                    <div>
                      <label htmlFor="city" className={labelClass}>Ville</label>
                      <input type="text" id="city" name="city" value={form.city} onChange={handleChange} className={inputClass} placeholder="Troyes" />
                    </div>
                    <div>
                      <label htmlFor="website" className={labelClass}>Site internet</label>
                      <input type="url" id="website" name="website" value={form.website} onChange={handleChange} className={inputClass} placeholder="https://..." />
                    </div>
                    <div>
                      <label htmlFor="socials" className={labelClass}>Réseaux sociaux</label>
                      <input type="text" id="socials" name="socials" value={form.socials} onChange={handleChange} className={inputClass} placeholder="Instagram, TikTok, YouTube..." />
                    </div>
                    <div className="md:col-span-2">
                      <label htmlFor="artisticDomain" className={labelClass}>Domaine artistique *</label>
                      <select id="artisticDomain" name="artisticDomain" value={form.artisticDomain} onChange={handleChange} className={inputClass} required>
                        <option value="">Sélectionner votre univers</option>
                        <option value="animateur-micro">Animateur Micro</option>
                        <option value="musique">Art de la Musique</option>
                        <option value="art-rue">Art de la Rue</option>
                        <option value="art-scene">Art de la Scène</option>
                        <option value="cirque">Art du Cirque</option>
                        <option value="arts-visuels">Arts Visuels</option>
                        <option value="enfants">Enfants</option>
                        <option value="sport-spectacle">Sport Spectacle</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label htmlFor="mediaLink" className={labelClass}>Lien vers vos photos / vidéos</label>
                      <input
                        type="url"
                        id="mediaLink"
                        name="mediaLink"
                        value={form.mediaLink}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="https://wetransfer.com/... ou Google Drive, Dropbox, SwissTransfer..."
                      />
                      <p className="mt-1.5 text-[11px] text-muted-foreground">Collez ici un lien WeTransfer, Google Drive, Dropbox ou tout autre service de partage de fichiers.</p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="message" className={labelClass}>{form.subject === 'artiste' ? 'Présentez-vous (parcours, spécialités, références)' : 'Votre projet'} *</label>
                <textarea id="message" name="message" value={form.message} onChange={handleChange} rows={6} className={`${inputClass} resize-none`} placeholder={form.subject === 'artiste' ? 'Décrivez votre univers, votre parcours, vos prestations types...' : "Décrivez votre événement, la date souhaitée, le nombre d'invités..."} required />
              </div>

              <button type="submit" disabled={sending} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3.5 rounded-full font-heading font-medium text-sm hover:bg-primary/90 transition-colors disabled:opacity-60">
                {sending ? 'Envoi en cours…' : 'Envoyer'} <Send size={14} />
              </button>

              <div className="mt-6 p-4 rounded-xl border border-dashed border-border bg-muted/30 flex items-start gap-3">
                <Music size={16} className="text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Vous êtes artiste ?</strong> Vous souhaitez proposer vos services et rejoindre notre réseau de talents ? Sélectionnez « Proposer mes services en tant qu'artiste » dans le type de prestation ci-dessus, ou{' '}
                  <button type="button" onClick={() => setForm(f => ({ ...f, subject: 'artiste' }))} className="text-primary hover:underline font-medium">cliquez ici</button>.
                </p>
              </div>
            </form>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h3 className="font-heading font-semibold text-sm text-foreground mb-4">Coordonnées</h3>
              <ul className="space-y-4">
                <li className="flex items-center gap-3 text-sm text-muted-foreground">
                  <Phone size={16} className="text-primary shrink-0" strokeWidth={1.5} />
                  <a href="tel:+33671351550" className="hover:text-primary transition-colors">06 71 35 15 50</a>
                </li>
                <li className="flex items-center gap-3 text-sm text-muted-foreground">
                  <MessageCircle size={16} className="text-primary shrink-0" strokeWidth={1.5} />
                  <a
                    href={buildWhatsAppUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary transition-colors"
                  >
                    WhatsApp · 06 71 35 15 50
                  </a>
                </li>
                <li className="flex items-center gap-3 text-sm text-muted-foreground">
                  <Mail size={16} className="text-primary shrink-0" strokeWidth={1.5} />
                  <a href="mailto:contact@cygnatureproduction.fr" className="hover:text-primary transition-colors">contact@cygnatureproduction.fr</a>
                </li>
                <li className="flex items-start gap-3 text-sm text-muted-foreground">
                  <MapPin size={16} className="text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
                  <a href="https://maps.app.goo.gl/SPqj82CAvgbDAqUU6" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
                    8 Rue du Champ de la Gaule<br />10120 Saint-Pouange
                  </a>
                </li>
                <li className="flex items-start gap-3 text-sm text-muted-foreground">
                  <Clock size={16} className="text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
                  <span>Lun-Ven : 9h-19h<br />Sam : 10h-18h<br />Dim : Fermé</span>
                </li>
              </ul>
            </div>

            <div className="p-5 rounded-xl border border-border bg-card">
              <h3 className="font-heading font-semibold text-sm text-foreground mb-2">Devis gratuit sous 48 à 72h</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Chaque projet est unique. Nous étudions votre demande avec attention et vous proposons un devis personnalisé et détaillé.
              </p>
            </div>

            <a href="https://www.evenementielpourtous.com/cygnature-production-126315" target="_blank" rel="noopener noreferrer" className="flex flex-col gap-4 p-5 rounded-xl border border-primary/20 bg-gradient-to-r from-primary/5 to-transparent hover:border-primary/40 hover:shadow-md transition-all">
              <div>
                <h3 className="font-heading font-semibold text-sm text-foreground mb-2 inline-flex items-center gap-2"><Award size={16} className="text-primary" strokeWidth={1.5} /> Event Awards 2023 — 2026</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">4 distinctions consécutives — Evenementiel pour tous</p>
              </div>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                {[eventAward2023, eventAward2024, eventAward2025, eventAward2026].map((award, i) => (
                  <img key={i} src={award} alt={`Event Award ${2023 + i}`} className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-contain" loading="lazy" />
                ))}
              </div>
            </a>

            <div>
              <h3 className="font-heading font-semibold text-sm text-foreground mb-3">Nous trouver</h3>
              <a
                href="https://maps.app.goo.gl/SPqj82CAvgbDAqUU6"
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-xl overflow-hidden border border-border hover:shadow-md transition-shadow cursor-pointer"
                title="Cliquez pour obtenir l'itinéraire"
              >
                <iframe
                  src="https://maps.google.com/maps?q=CYgnature+Production+Saint-Pouange&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  className="w-full h-48 pointer-events-none"
                  loading="lazy"
                  title="Cygnature Production — Saint-Pouange"
                />
                <div className="px-4 py-2.5 bg-card flex items-center gap-2 text-xs text-primary font-heading font-semibold">
                  <MapPin size={14} /> Voir l'itinéraire
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Nos Coordonnées */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-primary font-heading font-bold text-xs uppercase tracking-widest">Contact</span>
            <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground mt-2">Nos Coordonnées</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { icon: <Phone size={28} strokeWidth={1.5} />, label: 'Téléphone', value: '06 71 35 15 50', href: 'tel:+33671351550' },
              { icon: <MessageCircle size={28} strokeWidth={1.5} />, label: 'WhatsApp', value: '06 71 35 15 50', href: buildWhatsAppUrl(), external: true },
              { icon: <Mail size={28} strokeWidth={1.5} />, label: 'Email', value: 'contact@cygnatureproduction.fr', href: 'mailto:contact@cygnatureproduction.fr' },
              { icon: <MapPin size={28} strokeWidth={1.5} />, label: 'Adresse', value: '8 Rue du Champ de la Gaule\n10120 Saint-Pouange', href: 'https://maps.app.goo.gl/SPqj82CAvgbDAqUU6', external: true },
              { icon: <Clock size={28} strokeWidth={1.5} />, label: 'Horaires', value: 'Lun-Ven : 09h-20h\nSam : 10h-18h' },
            ].map((item) => (
              <div
                key={item.label}
                className="bg-card border border-border rounded-2xl p-8 text-center flex flex-col items-center hover:shadow-md transition-shadow"
              >
                <div className="w-14 h-14 rounded-full border border-primary/20 flex items-center justify-center text-primary mb-5">
                  {item.icon}
                </div>
                <h3 className="font-heading font-bold text-sm text-foreground mb-2">{item.label}</h3>
                {item.href ? (
                  <a href={item.href} className="text-sm text-muted-foreground hover:text-primary transition-colors whitespace-pre-line">{item.value}</a>
                ) : (
                  <p className="text-sm text-muted-foreground whitespace-pre-line">{item.value}</p>
                )}
              </div>
            ))}
          </div>

          {/* Bandeau urgence */}
          <div className="mt-14 max-w-4xl mx-auto">
            <div className="relative bg-card border border-border rounded-2xl px-8 py-10 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary rounded-tl-2xl" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-primary rounded-br-2xl" />
              <div>
                <h3 className="font-heading font-bold text-lg text-foreground">Un événement urgent ?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Appelez ou écrivez à Cyrille au <a href="tel:+33671351550" className="text-primary font-semibold hover:underline">06 71 35 15 50</a>
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="tel:+33671351550"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border-2 border-petrol text-petrol font-heading font-bold text-sm hover:bg-petrol hover:text-petrol-foreground transition-colors"
                >
                  <Phone size={16} /> Appeler
                </a>
                <a
                  href={buildWhatsAppUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border-2 border-primary text-primary font-heading font-bold text-sm hover:bg-primary hover:text-primary-foreground transition-colors"
                >
                  <MessageCircle size={16} /> WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
